import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Chip, Stack, Tooltip } from '@mui/material';

import type { AppointmentType } from '../../types';
import { ColorDot } from '../ColorDot';

export interface LinkedTypeChipsProps {
  /** Appointment types currently linked to the template. */
  linkedTypes: AppointmentType[];
  /** Opens the appointment-type editor for the clicked type. */
  onTypeClick: (appointmentType: AppointmentType) => void;
  /** Opens the link-appointment-type modal. */
  onLinkClick: () => void;
  size?: 'small' | 'medium';
}

/**
 * Chip row of the appointment types using a note template, plus a trailing
 * "+ Link appointment type" affordance. Presentational — the parent owns the
 * data and the modals the chips open.
 */
export const LinkedTypeChips: React.FC<LinkedTypeChipsProps> = ({
  linkedTypes,
  onTypeClick,
  onLinkClick,
  size = 'medium',
}) => (
  <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
    {linkedTypes.map(appointmentType => (
      // Tooltip signals the chip is interactive — the type editor is where you
      // change or remove the template link (this surface never unlinks directly).
      <Tooltip key={appointmentType.encounter_type_id} title="Edit — change or remove its template">
        <Chip
          size={size}
          variant="outlined"
          icon={<ColorDot colorEnum={appointmentType.color} size={12} />}
          label={appointmentType.encounter_type}
          onClick={() => onTypeClick(appointmentType)}
          aria-label={`Edit appointment type ${appointmentType.encounter_type}`}
          // Doubled ampersand outranks MUI's own `.MuiChip-icon` margin so the
          // leading dot gets a bit more breathing room than the default.
          sx={{ '&& .MuiChip-icon': { ml: 1 } }}
        />
      </Tooltip>
    ))}
    <Chip
      size={size}
      variant="outlined"
      icon={<AddIcon fontSize="small" />}
      label="Link appointment type"
      onClick={onLinkClick}
      aria-label="Link appointment type"
      sx={{ borderStyle: 'dashed', color: 'text.secondary', '&& .MuiChip-icon': { ml: 1 } }}
    />
  </Stack>
);
